// Order/PaymentTypePicker.tsx
import React, {useEffect, useState} from 'react';
import {axios} from "Auth/axiosConfig";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import {ICurrency, ICurrencyWithPrice, IPaymentSystem, IProductPrice} from "../../Types/commerce/shop";
import RadioLine from "Core/components/elements/RadioLine";
import logoTBank from '../../Static/img/icon/tbank/logo.svg';
import RadioCustomLine from "Core/components/elements/RadioCustomLine";
import {FRCC} from "WideLayout/Layouts";
import {useTheme} from "Theme/ThemeContext";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TelegramIcon from "@mui/icons-material/Telegram";

interface PaymentTypePickerProps {
    prices: IProductPrice[];
    setPaymentCurrency: (currency: ICurrencyWithPrice | null) => void;
    setPaymentSystem: (system: IPaymentSystem | null) => void;
    excluded_payment_systems?: IPaymentSystem[];
}

const PaymentTypePicker: React.FC<PaymentTypePickerProps> = ({
                                                                 prices,
                                                                 setPaymentCurrency,
                                                                 setPaymentSystem,
                                                                 excluded_payment_systems = []
                                                             }) => {
    const [selectedCurrency, setSelectedCurrency] = useState<string>('');
    const [paymentTypes, setPaymentTypes] = useState<{ [key: string]: IPaymentSystem[] }>({});
    const [selectedPaymentType, setSelectedPaymentType] = useState<IPaymentSystem | null>(null);
    const {byResponse} = useErrorProcessing();
    const {plt} = useTheme();

    useEffect(() => {
        axios.get('/api/v1/payment/types/')
            .then(response => setPaymentTypes(response.data))
            .catch(error => byResponse(error));
    }, [byResponse]);

    useEffect(() => {
        if (Object.keys(paymentTypes).length === 0) return;
        const rubPrice = prices.find(price => price.currency === 'RUB');
        if (rubPrice) {
            const initialCurrency: ICurrencyWithPrice = {currency: 'RUB', priceObject: rubPrice};
            setSelectedCurrency('RUB');
            setPaymentCurrency(initialCurrency);

            const availablePaymentTypes = paymentTypes['RUB'];
            if (availablePaymentTypes?.length === 1) {
                setSelectedPaymentType(availablePaymentTypes[0]);
                setPaymentSystem(availablePaymentTypes[0]);
            } else {
                setSelectedPaymentType(null);
                setPaymentSystem(null);
            }
        }
    }, [prices, paymentTypes, setPaymentCurrency, setPaymentSystem]);

    const handleCurrencyChange = (currency: string | number) => {
        const currencyStr = currency as ICurrency;
        setSelectedCurrency(currencyStr);
        const priceObject = prices.find(price => price.currency === currencyStr);
        const currencyWithPrice: ICurrencyWithPrice = {currency: currencyStr, priceObject};
        setPaymentCurrency(currencyWithPrice);

        const availablePaymentTypes = paymentTypes[currencyStr];
        if (availablePaymentTypes?.length === 1) {
            setSelectedPaymentType(availablePaymentTypes[0]);
            setPaymentSystem(availablePaymentTypes[0]);
        } else {
            setSelectedPaymentType(null);
            setPaymentSystem(null);
        }
    };

    const handlePaymentTypeChange = (paymentType: string | number) => {
        const paymentTypeStr = paymentType as string;
        setSelectedPaymentType(paymentTypeStr as IPaymentSystem);
        setPaymentSystem(paymentTypeStr as IPaymentSystem);
    };

    const filteredPaymentTypes =
        paymentTypes[selectedCurrency]?.filter(
            (pt) => !excluded_payment_systems.includes(pt)
        ) || [];

    useEffect(() => {
        if (filteredPaymentTypes.length > 0) {
            if (!selectedPaymentType || !filteredPaymentTypes.includes(selectedPaymentType)) {
                const firstPayment = filteredPaymentTypes[0];
                setSelectedPaymentType(firstPayment);
                setPaymentSystem(firstPayment);
            }
        } else {
            setSelectedPaymentType(null);
            setPaymentSystem(null);
        }
    }, [filteredPaymentTypes, selectedPaymentType, setPaymentSystem]);

    return (
        <div className={'fc gap-2'}>
            <div className="fc">
                <span>Выберите валюту</span>
                <RadioLine
                    options={[
                        ...prices.map(price => ({value: price.currency, label: price.currency})),
                        {value: 'other', label: 'Другое'}
                    ]}
                    selectedValue={selectedCurrency}
                    onChange={handleCurrencyChange}
                    className="w-100"
                    itemClass={'flex-grow-1'}
                />
            </div>


            {selectedCurrency && filteredPaymentTypes?.length > 0 && selectedPaymentType != 'stripe' && (
                <RadioCustomLine
                    options={filteredPaymentTypes.map(paymentType => ({
                        value: paymentType,
                        content: paymentType.includes('tbank')
                            ? (
                                <FRCC
                                    cls={'ftrans-300-eio'}
                                    px={1.2} pt={'.3rem'} pb={'.2rem'}
                                    rounded={3} g={'.4rem'}
                                    bg={plt.bg.primary35}
                                    boxShadow={paymentType === selectedPaymentType
                                        ? '0 0 3px 1px' + plt.colors.secondary.main
                                        : ''}
                                >
                                    <img
                                        src={logoTBank}
                                        alt={`Иконка ${paymentType}`}
                                        style={{maxHeight: 16, marginTop: 1}}
                                    />
                                    <span style={{
                                        fontWeight: 800,
                                        fontSize: '1.035rem',
                                    }}>
                                        {paymentType === "tbank"
                                            ? 'TBank'
                                            : paymentType === "tbank_installment"
                                                ? 'РАССРОЧКА'
                                                : ''}
                                    </span>
                                </FRCC>
                            )
                            : <span>{paymentType}</span>
                    }))}
                    maxWidth={300}
                    selectedValue={selectedPaymentType}
                    onChange={(val) => handlePaymentTypeChange(String(val))}
                    className="w-100 gap-2 flex-wrap mt-1"
                    itemClass={''}
                />
            )}

            {['USD', 'EUR', 'other'].includes(selectedCurrency) && (
                <div className="fc gap-1 mt-2">
                    <span>Вы можете оплатить через:</span>
                    <div className="frcc gap-3">
                        <a
                            href={`https://wa.me/79297600963?text=${encodeURIComponent(
                                `Здравствуйте, я хочу купить пакет консультаций в ${selectedCurrency}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="frcc gap-1 text-decoration-none"
                        >
                            <WhatsAppIcon style={{width: '40px', height: '40px', cursor: 'pointer', color: '#25D366'}}/>
                        </a>
                        <a
                            href={`https://t.me/share/url?url=&text=${encodeURIComponent(
                                `Здравствуйте, я хочу купить пакет консультаций в ${selectedCurrency}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="frcc gap-1 text-decoration-none"
                        >
                            <TelegramIcon style={{width: '40px', height: '40px', cursor: 'pointer', color: '#48b9ff'}}/>
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentTypePicker;
